import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  email,
  toEmailLocale,
  type EmailLocale,
  type NewsletterSource,
  type SubscribeResult,
} from '../../lib/email'
import Button from '../ui/Button'
import { track } from '../../lib/analytics/track'

type Variant = 'footer' | 'modal' | 'page'

interface NewsletterFormProps {
  variant?: Variant
  /** Override the source field. Defaults to the variant. */
  source?: NewsletterSource
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

export default function NewsletterForm({ variant = 'footer', source }: NewsletterFormProps) {
  const { t, i18n } = useTranslation()
  const { lang } = useParams<{ lang: string }>()

  const [emailValue, setEmailValue] = useState('')
  const [consent, setConsent] = useState(false)
  const [honeypot, setHoneypot] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errorKey, setErrorKey] = useState<string>('')

  // URL :lang is the source of truth (LangLayout syncs i18n to it); fall back
  // to the detected i18n language when the route has no lang segment.
  const locale: EmailLocale = toEmailLocale(lang ?? i18n.language)
  const tone = variant === 'footer' ? 'dark' : 'light'

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    if (status === 'submitting') return

    if (!consent) {
      setStatus('error')
      setErrorKey('newsletter.errorConsent')
      return
    }

    setStatus('submitting')
    setErrorKey('')

    const result: SubscribeResult = await email.subscribe({
      email: emailValue,
      locale,
      source: source ?? variant,
      honeypot,
    })

    if (result.ok) {
      track('newsletter_signup', { source: source ?? variant })
      setStatus('success')
      setEmailValue('')
      setConsent(false)
      return
    }

    setStatus('error')
    switch (result.reason) {
      case 'invalid_email':
        setErrorKey('newsletter.errorInvalid')
        break
      case 'rate_limited':
        setErrorKey('newsletter.errorRateLimited')
        break
      case 'network':
        setErrorKey('newsletter.errorNetwork')
        break
      default:
        setErrorKey('newsletter.errorServer')
    }
  }

  if (status === 'success') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={
          tone === 'dark'
            ? 'font-body text-sm text-ivory/85 leading-relaxed'
            : 'font-body text-sm text-mocha leading-relaxed'
        }
      >
        {t('newsletter.success')}
      </div>
    )
  }

  const inputBase =
    tone === 'dark'
      ? 'w-full bg-ivory/5 border border-ivory/20 focus:border-gold text-sm text-ivory placeholder:text-ivory/50 px-3 py-2.5 outline-none transition-colors rounded'
      : 'w-full bg-ivory border border-mocha/15 focus:border-gold text-sm text-mocha placeholder:text-mocha/40 px-3 py-2 outline-none transition-colors rounded'

  const labelClass =
    tone === 'dark'
      ? 'block font-body text-[10px] uppercase tracking-[0.25em] text-gold mb-2'
      : 'block font-body text-[10px] uppercase tracking-[0.25em] text-mocha/55 mb-2'

  const consentClass =
    tone === 'dark'
      ? 'font-body text-sm text-ivory/55 leading-relaxed'
      : 'font-body text-sm text-mocha/65 leading-relaxed'

  const errorClass =
    tone === 'dark'
      ? 'font-body text-xs text-gold mt-2'
      : 'font-body text-xs text-red-700 mt-2'

  return (
    <form onSubmit={handleSubmit} noValidate aria-label={t('newsletter.heading')}>
      {variant !== 'footer' && (
        <h3 className="font-display text-2xl text-mocha mb-2">{t('newsletter.heading')}</h3>
      )}
      {variant !== 'footer' && (
        <p className="font-body text-sm text-mocha/70 mb-4">{t('newsletter.subheading')}</p>
      )}

      <label className={labelClass} htmlFor={`newsletter-email-${variant}`}>
        {t('newsletter.emailLabel')}
      </label>
      <input
        id={`newsletter-email-${variant}`}
        type="email"
        required
        autoComplete="email"
        inputMode="email"
        placeholder={t('newsletter.emailPlaceholder')}
        value={emailValue}
        onChange={(e) => setEmailValue(e.target.value)}
        className={inputBase}
        disabled={status === 'submitting'}
      />

      {/* Honeypot - hidden from humans, harvested by bots. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor={`newsletter-website-${variant}`}>Website</label>
        <input
          id={`newsletter-website-${variant}`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="sr-only"
          disabled={status === 'submitting'}
        />
        <span
          aria-hidden="true"
          className={[
            'mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border transition-colors',
            tone === 'dark'
              ? consent ? 'bg-gold border-gold' : 'bg-transparent border-ivory/40'
              : consent ? 'bg-gold border-gold' : 'bg-transparent border-mocha/30',
          ].join(' ')}
        >
          {consent && (
            <svg viewBox="0 0 10 8" fill="none" className="w-full h-full p-0.5">
              <path d="M1 4l3 3 5-6" stroke={tone === 'dark' ? '#1a1208' : '#fff'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span className={consentClass}>{t('newsletter.consentLabel')}</span>
      </label>

      <div className="mt-4">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          tone={tone}
          disabled={status === 'submitting'}
          className="disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'submitting' ? t('newsletter.submitting') : t('newsletter.submit')}
        </Button>
      </div>

      {status === 'error' && errorKey && (
        <p role="alert" className={errorClass}>
          {t(errorKey)}
        </p>
      )}
    </form>
  )
}
